import React, { useEffect, useMemo } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { updatePartialState, useCategories, usePartialState } from '../../store/state';
import { queryCategories, queryModelState } from '../../store/actions';
import { BASETYPE_NAME_TO_VALUE } from '../../constants';
import { Filter } from '../base/Filter';
import { ModelList } from '../base/List';

const sortOptions = [
  { value: '1', label: translate('model_recently_favorite') },
  { value: '2', label: translate('model_sort_option_most_used') },
];

const cardSize = { width: 185, height: 272 };

export const Favorites = React.memo(() => {
  const { filters, data } = usePartialState('lora.favorites');
  const categories = useCategories((state) => state.lora);

  const listData = useMemo(() => {
    const items = data.items
      .filter((item) => {
        if (filters.category !== '0' && !item.tags?.some((tag) => tag.code === filters.category)) {
          return false;
        }

        if (filters.keyword) {
          const targetString = `${item.name || ''}${item.userName || ''}${item.tagsString || ''}`.toLowerCase();

          if (!targetString.includes(filters.keyword.toLowerCase())) {
            return false;
          }
        }

        if (filters.baseTypes?.length && !filters.baseTypes.includes(String(BASETYPE_NAME_TO_VALUE[item.baseType]))) {
          return false;
        }

        return true;
      })
      .filter((item, index, array) => array.findIndex((t) => t?.versionId === item?.versionId) === index);

    return { items, total: items.length, loading: data.loading, hasMore: data.hasMore };
  }, [data, filters]);

  const categoryCounts = useMemo(() => {
    return data.items.reduce((acc, item) => {
      item.tags?.forEach((tag) => {
        acc[tag.code] = acc[tag.code] ? acc[tag.code] + 1 : 1;
      });
      return acc;
    }, {});
  }, [data.items]);

  categoryCounts['0'] = data.items.length;

  const handleApplyFilter = (value) => {
    updatePartialState('lora.favorites.filters', value);

    if (value.sortType) {
      updatePartialState('lora.favorites.data', { items: [], hasMore: true });
      queryModelState('lora', 'favorites', {}, false);
    }
  };

  useEffect(() => {
    queryCategories('lora');
    updatePartialState('lora.favorites.filters', { page: 1 });
    updatePartialState('lora.favorites.data', { items: [], hasMore: true });
    queryModelState('lora', 'favorites');
  }, []);

  return (
    <main className="flex items-stretch flex-1 overflow-hidden">
      <aside className="flex flex-col items-stretch w-[150px] p-2 space-y-1 border-r border-[#efefef]">
        {categories.catalogArr.map((item, idx) => (
          <button
            key={item.code}
            disabled={filters.category === item.code}
            onClick={() => handleApplyFilter({ category: item.code })}
            className="flex items-center py-[8px] px-3 rounded text-[13px] leading-[18px] whitespace-normal break-words text-left overflow-hidden text-[#7f7f7f] hover:bg-[#F4F6F8] disabled:bg-primary/15 disabled:text-[#2d2d2d] disabled:font-semibold"
          >
            {idx === 0 ? translate('history_tab1') : item.tagLabel}({categoryCounts[item.code] || 0})
          </button>
        ))}
      </aside>
      <div className="flex flex-col flex-1">
        <div
          className="relative z-[2] flex items-center justify-between px-5 py-2 "
          style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.05)' }}
        >
          <b className="text-sm text-[#2d2d2d] translate-y-0.5">{translate('common_fav')}</b>
          <Filter value={filters} sortOptions={sortOptions} onSubmit={handleApplyFilter} />
        </div>
        <div className="flex-1 px-4 overflow-auto tiny-scrollbar bg-[#f9fbfb]">
          <ModelList data={listData} cols={4} showTag={!filters.keyword} cardSize={cardSize} from="favorite" />
        </div>
      </div>
    </main>
  );
});
